//
//  lookupGuides.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module builds the guides shared by the settings lookup pages.
//

import type { Permission } from "src/api/Tmd.dtos";

import {
    actionsStop,
    column,
    field,
    filterStop,
    grid,
    saveStop,
    type PageGuide,
    type PageGuideColumn,
    type PageGuideStop
} from "./pageGuide";

/** Describes one lookup list, its entries and where they are used. */
export type LookupDescription = {
    /** Plural noun, such as "trial phases". */
    plural: string;
    /** Singular noun, such as "trial phase". */
    singular: string;
    /** What an entry means, in a sentence. */
    purpose: string;
    /** Where entries are offered, such as "the Phase field on trial details". */
    usedIn: string;
    /** An example entry name, to make the Name field concrete. */
    example?: string;
    createPermission: Permission;
    updatePermission: Permission;
    extraColumns?: PageGuideColumn[];
    extraFields?: PageGuideStop[];
    rules?: string[];
};

/** The list page of a lookup. */
export const lookupListGuide = (lookup: LookupDescription): PageGuide => ({
    intro: `The list of ${lookup.plural}. ${lookup.purpose} They are offered in ${lookup.usedIn}.`,
    stops: [
        filterStop(
            `Type part of a name to filter the list. Inactive ${lookup.plural} are hidden unless you switch on inactive records; they stay on the records that already use them.`
        ),
        actionsStop(
            `Create opens a blank ${lookup.singular}. Nothing is added until you submit it.`,
            lookup.createPermission
        ),
        grid(
            `Each row is one ${lookup.singular}. Select a row to edit it. Greyed rows are inactive.`,
            [column("Name", `The name shown in ${lookup.usedIn}.`), ...(lookup.extraColumns ?? [])]
        )
    ],
    notes: [
        `${capitalise(lookup.plural)} are never deleted. Deactivate one instead: it disappears from new selections but existing records keep it, and it can be reactivated at any time.`
    ]
});

/** The edit page of a lookup entry. */
export const lookupItemGuide = (lookup: LookupDescription): PageGuide => ({
    intro: `Change one ${lookup.singular}. ${lookup.purpose} Changes apply everywhere the ${lookup.singular} is already used.`,
    stops: [
        field(
            "value",
            "Name",
            `The name people pick from ${lookup.usedIn}${
                lookup.example ? `, for example "${lookup.example}"` : ""
            }. Renaming it renames it on every record that uses it.`
        ),
        ...(lookup.extraFields ?? []),
        field(
            "rank",
            "Rank",
            "The position in drop-down lists; lower ranks are listed first. Leave gaps (10, 20, 30) so entries can be inserted later."
        ),
        field(
            "active",
            "Active",
            "Clear to retire the entry. It is no longer offered for new records, but records that already use it keep it."
        ),
        saveStop(
            "Submit saves and stays on this page; Submit And Return saves and goes back to the list.",
            lookup.updatePermission
        )
    ],
    rules: lookup.rules
});

/** The create page of a lookup entry. */
export const lookupCreateGuide = (lookup: LookupDescription): PageGuide => ({
    intro: `Add a new ${lookup.singular}. ${lookup.purpose} Once saved it is offered in ${lookup.usedIn}.`,
    stops: [
        field(
            "value",
            "Name",
            `The name people will pick from ${lookup.usedIn}${
                lookup.example ? `, for example "${lookup.example}"` : ""
            }. Check the list first so the same entry is not added twice.`
        ),
        ...(lookup.extraFields ?? []),
        field(
            "rank",
            "Rank",
            "Where the entry appears in drop-down lists; lower ranks are listed first. Pick a number between the neighbours you want it to sit between."
        ),
        field("active", "Active", "Leave ticked so the entry can be chosen straight away."),
        saveStop(
            `Submit creates the ${lookup.singular} and opens it for editing; Submit And Return creates it and goes back to the list.`,
            lookup.createPermission
        )
    ],
    rules: lookup.rules,
    notes: [
        `The new ${lookup.singular} is available as soon as it is saved. It cannot be deleted later, only deactivated.`
    ]
});

/** Builds the list, edit and create guides of one lookup keyed by their routes. */
export const lookupGuides = (
    routes: { list: string; item: string; create: string },
    lookup: LookupDescription
): Record<string, PageGuide> => ({
    [routes.list]: lookupListGuide(lookup),
    [routes.item]: lookupItemGuide(lookup),
    [routes.create]: lookupCreateGuide(lookup)
});

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
